import { useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { useAuth, useUser } from "@clerk/react";
import { toast } from "sonner";
import {
  Share2Icon,
  Trash2Icon,
  Settings2Icon,
  WandSparklesIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Item, ItemContent, ItemDescription, ItemTitle, ItemList } from "@/components/ui/item";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
} from "@/components/ui/frame";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCampaignStore } from "@/stores";
import { useShareStore, ShareTab, CreatorOptions } from "@/stores/shareStore";
import { CopyField } from "./CopyField";

const SECTIONS: { id: ShareTab; label: string; description: string }[] = [
  {
    id: "creators",
    label: "Creators",
    description: "Read-only roster of creators and their stats.",
  },
  {
    id: "brief",
    label: "Brief",
    description: "The campaign brief content.",
  },
  {
    id: "analytics",
    label: "Analytics",
    description: "Conversion funnel and video performance metrics.",
  },
];

// Sub-options nested under the Creators section.
const CREATOR_SUBOPTIONS: {
  key: keyof CreatorOptions;
  label: string;
  description: string;
}[] = [
  {
    key: "show_stats",
    label: "Performance",
    description: "Show follower/view metrics and country.",
  },
  {
    key: "show_fields",
    label: "Fields & status",
    description: "Show collected field values and pipeline status.",
  },
  {
    key: "show_conversations",
    label: "Conversations",
    description: "Show the email conversation with each creator.",
  },
];

const NO_OPTIONS: CreatorOptions = {
  show_stats: false,
  show_fields: false,
  show_conversations: false,
};

function buildShareUrl(token: string) {
  // Served from the same origin as the app, at /share/{token}.
  return `${window.location.origin}/share/${token}`;
}

export function ShareLinkFrame() {
  const { campaignId } = useCampaignStore();
  const { getToken, orgId } = useAuth();
  const { user } = useUser();
  const { getShare, saveShare, deleteShare, generateDemo } = useShareStore();

  // Internal demo tooling — gated to Uplodio members (re-verified server-side).
  const isUplodioMember = Boolean(
    (user?.publicMetadata as Record<string, unknown> | undefined)?.uplodio_member
  );

  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState<{
    token: string;
    tabs: ShareTab[];
    creator_options: CreatorOptions;
  } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [staged, setStaged] = useState<Record<ShareTab, boolean>>({
    analytics: false,
    brief: false,
    creators: true,
  });
  const [stagedOptions, setStagedOptions] = useState<CreatorOptions>(NO_OPTIONS);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [demoUrl, setDemoUrl] = useState("");
  const [generatingDemo, setGeneratingDemo] = useState(false);

  useEffect(() => {
    if (!campaignId || !orgId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const token = await getToken();
      const existing = await getShare(campaignId, orgId, token);
      if (!cancelled) {
        setShare(existing);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, orgId, getToken, getShare]);

  const openDialog = () => {
    // Seed toggles from the existing link, or defaults for a new one.
    if (share) {
      setStaged({
        analytics: share.tabs.includes("analytics"),
        brief: share.tabs.includes("brief"),
        creators: share.tabs.includes("creators"),
      });
      setStagedOptions(share.creator_options ?? NO_OPTIONS);
    } else {
      setStaged({ analytics: false, brief: false, creators: true });
      setStagedOptions(NO_OPTIONS);
    }
    setDialogOpen(true);
  };

  const selectedTabs = SECTIONS.filter((s) => staged[s.id]).map((s) => s.id);
  const showConversations = staged.creators && stagedOptions.show_conversations;

  const handleSave = async () => {
    if (!campaignId || !orgId || selectedTabs.length === 0) return;
    setSaving(true);
    try {
      const token = await getToken();
      const options = staged.creators ? stagedOptions : NO_OPTIONS;
      const result = await saveShare(
        campaignId,
        orgId,
        selectedTabs,
        options,
        token
      );
      if (result) {
        setShare(result);
        setDialogOpen(false);
        toast.success("Share link saved");
      } else {
        toast.error("Failed to save share link");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!campaignId || !orgId) return;
    setDeleting(true);
    try {
      const token = await getToken();
      const ok = await deleteShare(campaignId, orgId, token);
      if (ok) {
        setShare(null);
        toast.success("Share link deleted");
      } else {
        toast.error("Failed to delete share link");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleGenerateDemo = async () => {
    if (!campaignId || !orgId) return;
    setGeneratingDemo(true);
    try {
      const token = await getToken();
      const res = await generateDemo(campaignId, orgId, demoUrl.trim(), token);
      if (res) {
        toast.success(
          `Generated ${res.generated} demo conversation${
            res.generated === 1 ? "" : "s"
          }`
        );
      } else {
        toast.error("Failed to generate demo conversations");
      }
    } finally {
      setGeneratingDemo(false);
    }
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Shareable Link</FrameTitle>
        <FrameDescription>A public, read-only link — no login needed.</FrameDescription>
      </FrameHeader>
      <FramePanel className="flex flex-row items-end justify-center gap-3">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Spinner className="size-4 text-muted-foreground" />
          </div>
        ) : share ? (
          <>
            <CopyField
              label="Public link"
              id="share-link"
              value={buildShareUrl(share.token)}
              className="w-full"
            />
            <div className="flex items-center justify-center gap-2 w-min">
              <Button size="sm" variant="secondary" onClick={openDialog}>
                <Settings2Icon />
                Manage link
              </Button>
              <Button
                size="sm"
                variant="destructive-ghost"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <Spinner /> : <Trash2Icon />}
                Delete
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center gap-3 py-2">
            {!share && (
              <Button size="sm" onClick={openDialog} variant="secondary">
                <Share2Icon />
                Create shareable link
              </Button>
            )}
          </div>
        )}
      </FramePanel>

      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {share ? "Manage shareable link" : "Create shareable link"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Choose which sections are visible on the public link. You can
              change these anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ItemList>
            {SECTIONS.map((section) => (
              <div key={section.id}>
                <Item variant="row" asChild>
                  <label htmlFor={`share-toggle-${section.id}`}>
                    <ItemContent className="gap-0">
                      <ItemTitle>{section.label}</ItemTitle>
                      <ItemDescription>{section.description}</ItemDescription>
                    </ItemContent>
                    <Switch
                      id={`share-toggle-${section.id}`}
                      checked={staged[section.id]}
                      onCheckedChange={(value) =>
                        setStaged((prev) => ({ ...prev, [section.id]: value }))
                      }
                    />
                  </label>
                </Item>

                {section.id === "creators" && staged.creators && (
                  <div className="flex flex-col border-l border-dashed border-muted-foreground/20 ml-5 mb-2">
                    {CREATOR_SUBOPTIONS.map((opt) => (
                      <Item key={opt.key} variant="row" asChild className="py-2 pl-4">
                        <label htmlFor={`share-opt-${opt.key}`}>
                          <ItemContent className="gap-0">
                            <ItemTitle className="font-normal">{opt.label}</ItemTitle>
                            <ItemDescription>{opt.description}</ItemDescription>
                          </ItemContent>
                          <Switch
                            id={`share-opt-${opt.key}`}
                            size="sm"
                            checked={stagedOptions[opt.key]}
                            onCheckedChange={(value) =>
                              setStagedOptions((prev) => ({
                                ...prev,
                                [opt.key]: value,
                              }))
                            }
                          />
                        </label>
                      </Item>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </ItemList>

          {isUplodioMember && share && showConversations && (
            <Frame>
              <FrameHeader>
                <FrameTitle>Demo conversations</FrameTitle>
                <FrameDescription>Internal: fabricate threads from a brand website.</FrameDescription>
              </FrameHeader>
              <FramePanel className="flex items-center gap-2 p-3">
                <Input
                  value={demoUrl}
                  onChange={(e) => setDemoUrl(e.target.value)}
                  placeholder="https://brand.com (defaults to org website)"
                  className="h-8 text-xs"
                  disabled={generatingDemo}
                />
                <Button
                  size="sm"
                  variant="neutral"
                  className="shrink-0"
                  onClick={handleGenerateDemo}
                  disabled={generatingDemo || !demoUrl.trim()}
                >
                  {generatingDemo ? (
                    <Spinner />
                  ) : (
                    <WandSparklesIcon />
                  )}
                  Generate
                </Button>
              </FramePanel>
            </Frame>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleSave();
              }}
              disabled={saving || selectedTabs.length === 0}
            >
              {saving && <Spinner />}
              Save Link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Frame>
  );
}
